import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';

const AdminComments = () => {
  const [reportedComments, setReportedComments] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReportedComments();
  }, []);

  const loadReportedComments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/comments/reported/all');
      setReportedComments(response.data);

      // Charger les informations des produits
      const productIds = [...new Set(response.data.map(c => c.product_id))];
      const productPromises = productIds.map(id => 
        api.get(`/products/${id}`).catch(() => null)
      );
      const productResponses = await Promise.all(productPromises);
      
      const productMap = {};
      productResponses.forEach((res, index) => {
        if (res) {
          productMap[productIds[index]] = res.data;
        }
      });
      setProducts(productMap);
    } catch (error) {
      console.error('Error loading reported comments:', error);
      toast.error('Erreur lors du chargement des avis signalés');
    } finally {
      setLoading(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteComment = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/comments/${deleteTarget.id}`);
      toast.success('Avis supprimé avec succès');
      setDeleteTarget(null);
      loadReportedComments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            Avis Signalés
          </h1>
          <p className="text-muted-foreground mt-2">
            Gérer les avis professionnels signalés par les utilisateurs
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : reportedComments.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              Aucun avis signalé pour le moment
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Produit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Auteur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Avis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Signalements
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reportedComments.map((comment) => {
                    const product = products[comment.product_id];
                    return (
                      <tr key={comment.id} className="hover:bg-secondary/20">
                        <td className="px-6 py-4">
                          <div className="font-medium text-sm">
                            {product?.intitule || 'Produit inconnu'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {product?.editeur || (product?.original_publishers || product?.publishers || []).map(p => p.name).filter(Boolean).join(' / ') || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {comment.user_name}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm max-w-md">
                            {comment.commentaire.length > 100 
                              ? comment.commentaire.substring(0, 100) + '...'
                              : comment.commentaire
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="text-sm font-semibold text-red-600">
                              {comment.nb_signalements}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteTarget(comment)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <DestructiveActionDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Supprimer l'avis"
        itemName={deleteTarget?.contenu?.substring(0, 50) || 'Avis signalé'}
        description="L'avis sera définitivement supprimé."
        onConfirm={handleDeleteComment}
      />
    </DashboardLayout>
  );
};

export default AdminComments;
